import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getShowsByCategory, categories } from '@/mocks/shows';
import type { Show } from '@/mocks/shows';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeTab, setActiveTab] = useState(slug || 'all');
  const [shows, setShows] = useState<Show[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 12;

  useEffect(() => {
    setActiveTab(slug || 'all');
    setCurrentPage(1);
  }, [slug]);

  useEffect(() => {
    setShows(getShowsByCategory(activeTab));
  }, [activeTab]);

  const currentCategory = categories.find(c => c.slug === activeTab);
  const totalPages = Math.ceil(shows.length / perPage);
  const paginatedShows = shows.slice((currentPage - 1) * perPage, currentPage * perPage);

  const handleTabChange = (catSlug: string) => {
    setActiveTab(catSlug);
    setCurrentPage(1);
    window.history.replaceState(null, '', `/category/${catSlug}`);
  };

  return (
    <main className="min-h-screen bg-background-50 pt-16">
      {/* Breadcrumb */}
      <nav className="px-4 md:px-8 py-4 max-w-[1280px] mx-auto" aria-label="面包屑导航">
        <ol className="flex items-center gap-2 text-sm">
          <li>
            <Link to="/" className="text-foreground-500 hover:text-primary-400 transition-colors duration-200 whitespace-nowrap">
              首页
            </Link>
          </li>
          <li className="text-foreground-300">
            <i className="ri-arrow-right-s-line text-xs"></i>
          </li>
          <li>
            <span className="text-foreground-800 font-medium whitespace-nowrap">
              {currentCategory?.name || '全部影视'}
            </span>
          </li>
        </ol>
      </nav>

      {/* Header */}
      <section className="px-4 md:px-8 pb-6 max-w-[1280px] mx-auto">
        <h1 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
          {currentCategory?.name || '全部影视'}
        </h1>
        <p className="text-sm text-foreground-500 mt-2">
          共 {shows.length} 部作品，海量高清影视资源免费在线观看
        </p>
      </section>

      {/* Category Tabs */}
      <div className="px-4 md:px-8 sticky top-16 z-30 bg-background-50/95 backdrop-blur-xl border-b border-foreground-50/5 py-3">
        <div className="max-w-[1280px] mx-auto flex items-center gap-1.5 overflow-x-auto scrollbar-none flex-wrap">
          {categories.map((cat) => (
            <button
              key={cat.slug}
              onClick={() => handleTabChange(cat.slug)}
              className={`px-4 py-2 text-sm rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${
                activeTab === cat.slug
                  ? 'bg-primary-500 text-background-950 font-semibold'
                  : 'bg-background-100 text-foreground-500 hover:bg-background-200 hover:text-foreground-700'
              }`}
            >
              <i className={`${cat.icon} text-xs`}></i>
              {cat.name}
            </button>
          ))}
        </div>
      </div>

      {/* Show Grid */}
      <section className="px-4 md:px-8 py-8 max-w-[1280px] mx-auto">
        {paginatedShows.length > 0 ? (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4">
              {paginatedShows.map((show) => (
                <Link
                  key={show.id}
                  to={`/detail/${show.id}`}
                  className="group block rounded-lg overflow-hidden bg-background-100 hover:bg-background-200 transition-all duration-300 cursor-pointer"
                  data-product-shop
                >
                  <div className="relative aspect-[2/3] overflow-hidden">
                    <img
                      src={show.coverImage}
                      alt={show.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      title={`${show.title} - ${show.englishTitle}`}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-transparent opacity-50"></div>
                    <div className="absolute top-2 right-2">
                      <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-background-950/70 text-primary-400 whitespace-nowrap">
                        {show.rating}
                      </span>
                    </div>
                    {show.isHot && (
                      <div className="absolute top-2 left-2">
                        <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-accent-500 text-background-950 whitespace-nowrap">
                          HOT
                        </span>
                      </div>
                    )}
                    <div className="absolute bottom-2 left-2">
                      <span className="text-[10px] text-foreground-700 bg-background-950/50 px-1.5 py-0.5 rounded whitespace-nowrap">
                        {show.currentEpisode}
                      </span>
                    </div>
                  </div>
                  <div className="p-2.5">
                    <h3 className="text-xs font-semibold text-foreground-950 group-hover:text-primary-400 transition-colors duration-200 line-clamp-1 whitespace-nowrap overflow-hidden text-ellipsis">
                      {show.title}
                    </h3>
                    <p className="text-[11px] text-foreground-500 mt-0.5 line-clamp-1 whitespace-nowrap overflow-hidden text-ellipsis">
                      {show.englishTitle}
                    </p>
                    <div className="flex items-center gap-1.5 mt-1.5">
                      <span className="text-[10px] text-foreground-400 bg-secondary-500/10 px-1 py-0.5 rounded whitespace-nowrap">
                        {show.category}
                      </span>
                      <span className="text-[10px] text-foreground-400 whitespace-nowrap">{show.year}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-10">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500 hover:bg-background-200 disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200 cursor-pointer"
                >
                  <i className="ri-arrow-left-s-line"></i>
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                  <button
                    key={p}
                    onClick={() => setCurrentPage(p)}
                    className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm transition-all duration-200 cursor-pointer ${
                      p === currentPage
                        ? 'bg-primary-500 text-background-950 font-semibold'
                        : 'bg-background-100 text-foreground-500 hover:bg-background-200'
                    }`}
                  >
                    {p}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500 hover:bg-background-200 disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200 cursor-pointer"
                >
                  <i className="ri-arrow-right-s-line"></i>
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-20">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-2xl bg-background-100">
              <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
            </div>
            <p className="text-foreground-500">暂无相关内容，请尝试其他分类</p>
          </div>
        )}
      </section>

      {/* Schema.org */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": `${currentCategory?.name || '全部影视'} - 爱丫爱丫影院`,
            "description": `爱丫爱丫影院${currentCategory?.name || '全部影视'}分类，海量高清影视资源免费在线观看。`,
            "url": typeof window !== 'undefined' ? window.location.href : '',
            "isPartOf": {
              "@type": "WebSite",
              "name": "爱丫爱丫影院",
              "url": "https://aiyaaiya.com/"
            },
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://aiyaaiya.com/" },
                { "@type": "ListItem", "position": 2, "name": currentCategory?.name || '全部影视' }
              ]
            }
          })
        }}
      />
    </main>
  );
}